import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useForm, Controller } from "react-hook-form";
import { Link } from "react-router-dom";
import { Link as ScrollLink, Events as ScrollEvents } from "react-scroll";
import {
  CardElement,
  IbanElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import numeral from "numeral";

import { makeOrder } from "../../configureStore/order";
import {
  getSubscriptionItem,
  getSharesItem,
  capitalizeFirstLetter,
  paymentMethods,
} from "../../utilities";
import {
  colors,
  menuOffset,
  GridWrapper,
  TextField,
  RadioField,
  Checkbox,
  Button,
} from "../../theme";
import {
  SupportContainer,
  SupportContent,
  SupportMain,
  SupportAside,
} from "./supportTheme";
import PluginError from "../PluginError";
import SEPAPayment from "./SupportSEPAPayment";
import SEPAPaymentMandate from "./SupportSEPAPaymentMandate";
import CardPayment from "./SupportCardPayment";
import CheckPayment from "./SupportCheckPayment";
import SubscriptionOffer from "./SupportSubscriptionOffer";
import SocioOffer from "./SupportSocioOffer";
import SharesOffer from "./SupportSharesOffer";
import FAQ from "./SupportFAQ";
import OverlayLoader from "../loader/OverlayLoader";

const Container = styled(SupportContainer)`
  padding: 70px 0;

  @media (max-width: 1023px) {
    padding: 40px 0 70px;
  }

  form section {
    margin: 55px auto;
  }

  form h3 {
    border-bottom: 1px solid ${colors.titleBorder};
    color: black;
    font-size: 2.1rem;
    font-weight: 400;
    padding-bottom: 8px;
    text-align: left;
    text-transform: uppercase;

    @media (max-width: 767px) {
      font-size: 1.8rem;
    }

    strong {
      color: ${colors.purple};
      font-weight: 700;
    }

    + p {
      line-height: 2.4rem;
      text-align: left;
    }
  }

  strong {
    color: ${colors.green};
  }
`;

const Shares = styled.fieldset`
  margin: auto;
  width: 50%;

  > div {
    margin-top: 30px;
  }

  .MuiFormLabel-root {
    font-size: 1.7rem;
  }

  .MuiInputBase-input {
    font-size: 2.4rem;
    text-align: center;
  }
`;

const SocioLink = styled(ScrollLink)`
  cursor: pointer;
  text-decoration: underline;
  transition: 0.3s;

  :hover {
    color: ${colors.primaryColor};
  }
`;

const Payment = styled.div`
  background: ${colors.lightGray};
  padding: 20px;

  > div {
    background: white;
    padding: 10px;
  }
`;

const Validation = styled.section`
  margin-bottom: 0 !important;
  text-align: left;

  ul {
    list-style: none;
    margin: 40px 20px 50px;
    padding: 0;
  }

  li {
    font-size: 1.65rem;
    font-weight: 600;
    line-height: 2.4rem;
    margin-bottom: 24px;

    @media (max-width: 767px) {
      font-size: 1.5rem;
    }
  }

  i {
    color: ${colors.green};
    margin-right: 12px;
  }

  fieldset {
    margin: 0;
    padding: 0;
  }
`;

function SupportPayment({
  dispatch,
  match,
  history,
  subscriptionPlans: { list: plans },
  order,
  account,
}) {
  const { register, errors, control, watch, setValue, handleSubmit } = useForm({
    defaultValues: {
      interval: "MONTH",
      price: "8.00",
      shares: 5,
      payment_method: "SEPA",
    },
  });
  const [selectedFAQ, setSelectedFAQ] = useState(null);
  const formValues = watch();
  const stripe = useStripe();
  const elements = useElements();

  const isSubscriptionPath = match.params.path === "abonnement";
  const isSocioPath = match.params.path === "socio";
  const isSharesPath = match.params.path === "parts-sociales";

  useEffect(() => {
    ScrollEvents.scrollEvent.register("end", (to) => {
      setSelectedFAQ(to);
    });

    return () => ScrollEvents.scrollEvent.remove("end");
  }, []);

  if (!stripe) {
    return <PluginError script="stripe" />;
  }

  function handleIntervalChange() {
    setValue("price", formValues.interval === "MONTH" ? "8.00" : "96.00");
    setValue("payment_method", "SEPA");
  }

  function getPlanProductId(data) {
    return plans.find(
      (plan) => plan.interval === data.interval && plan.price === data.price
    ).id;
  }

  function onSubmit(data) {
    const items = [];

    if (!account.active_subscription) {
      items.push(getSubscriptionItem(getPlanProductId(data)));
    }

    if (data.shares > 0) {
      items.push(getSharesItem(data.shares));
    }

    dispatch(
      makeOrder(items, data.payment_method, async (clientSecret) => {
        if (data.payment_method === "CREDIT_CARD") {
          return await stripe.confirmCardPayment(clientSecret, {
            payment_method: {
              card: elements.getElement(CardElement),
              billing_details: {
                name: account.firstname + " " + account.lastname,
                email: account.email,
              },
            },
          });
        } else if (data.payment_method === "SEPA") {
          return await stripe.confirmSepaDebitPayment(clientSecret, {
            payment_method: {
              sepa_debit: elements.getElement(IbanElement),
              billing_details: {
                name: account.firstname + " " + account.lastname,
                email: account.email,
              },
            },
          });
        }
      })
    ).then(({ error }) => {
      if (!error) {
        history.push("/compte", { modal: "PAYMENT_VALIDATION" });
      }
    });
  }

  return (
    <Container>
      {isSubscriptionPath && (
        <>
          <h1>
            <span>3.</span> Abonnement
          </h1>
          <p>
            Dernière étape pour devenir abonné du Média ! Choisissez quel
            abonnement vous convient.
          </p>
        </>
      )}
      {isSocioPath && (
        <>
          <h1>
            <span>3.</span> Souscription
          </h1>
          {!account.active_subscription ? (
            account.shares_owned.LEMEDIATV_SHARES_SCIC < 5 ? (
              <p>
                Dernière étape pour devenir Socio ! Choisissez quel abonnement
                vous convient et le nombre de parts sociales (5 minimum) que
                vous souhaitez acquérir.
              </p>
            ) : (
              <p>
                Dernière étape pour devenir Socio ! Choisissez quel abonnement
                vous convient et si vous souhaitez acquérir des parts sociales
                supplémentaires.
              </p>
            )
          ) : (
            <p>
              Dernière étape pour devenir Socio ! Choisissez le nombre de parts
              sociales (5 minimum) que vous souhaitez acquérir.
            </p>
          )}
        </>
      )}
      {isSharesPath && (
        <>
          <h1>Parts sociales</h1>
          <p>
            Pour devenir pleinement Socio de la coopérative Le Média vous devez
            posséder au minimum 5 parts sociales.
          </p>
        </>
      )}
      <SupportContent>
        <SupportMain>
          <OverlayLoader
            isVisible={order.isFetching}
            error={order.error != null}
          />
          <h2>Je choisis ma contribution</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            {!account.active_subscription && (
              <>
                <section>
                  <h3>
                    Quel <strong>rythme</strong> de paiement ?
                  </h3>
                  <p>Je paie mon abonnement mensuellement ou à l'année.</p>
                  <RadioField>
                    <input
                      type="radio"
                      name="interval"
                      value="MONTH"
                      id="interval_MONTH"
                      ref={register({ required: true })}
                      onChange={handleIntervalChange}
                    />
                    <label htmlFor="interval_MONTH">Mensuel</label>
                    <input
                      type="radio"
                      name="interval"
                      value="YEAR"
                      id="interval_YEAR"
                      ref={register({ required: true })}
                      onChange={handleIntervalChange}
                    />
                    <label htmlFor="interval_YEAR">Annuel</label>
                  </RadioField>
                </section>
                <section>
                  <h3>
                    Quel <strong>montant</strong> d'abonnement ?
                  </h3>
                  <p>
                    Je fixe moi-même la somme que je souhaite verser tous les{" "}
                    {formValues.interval === "MONTH" ? "mois" : "ans"}.
                  </p>

                  <RadioField>
                    {plans
                      .filter((plan) => plan.interval === formValues.interval)
                      .sort(
                        (a, b) => parseInt(a.price, 10) - parseInt(b.price, 10)
                      )
                      .map((plan, index) => (
                        <React.Fragment key={index}>
                          <input
                            type="radio"
                            name="price"
                            value={plan.price}
                            id={`price_${plan.price}`}
                            ref={register({ required: true })}
                          />
                          <label htmlFor={`price_${plan.price}`}>
                            {parseInt(plan.price, 10)}€
                          </label>
                        </React.Fragment>
                      ))}
                  </RadioField>
                </section>
              </>
            )}
            {(isSocioPath || isSharesPath) && (
              <section>
                <h3>
                  Combien de <strong>parts sociales</strong> ?
                </h3>
                <p>
                  En possédant au minimum 5 parts sociales de la coopérative Le
                  Média, je suis membre du{" "}
                  <SocioLink
                    to="SCIC"
                    offset={-Math.abs(parseInt(menuOffset, 10) + 83)}
                    isDynamic
                    smooth
                    duration={400}
                  >
                    collège des Socios
                  </SocioLink>
                  . 1 part sociale = 1€.
                </p>
                <Shares>
                  <Controller
                    as={TextField}
                    label="Parts sociales"
                    type="number"
                    name="shares"
                    control={control}
                    rules={{
                      required: "Le nombre de parts sociales est requis",
                      min: {
                        value:
                          account.shares_owned.LEMEDIATV_SHARES_SCIC >= 5
                            ? isSharesPath
                              ? 1
                              : 0
                            : 5 - account.shares_owned.LEMEDIATV_SHARES_SCIC,
                        message:
                          account.shares_owned.LEMEDIATV_SHARES_SCIC >= 5
                            ? isSharesPath
                              ? "Vous devez au minimum prendre 1 part sociale."
                              : "Vous ne pouvez pas prendre un nombre négatif de part sociale."
                            : `Vous devez au minimum prendre ${
                                5 - account.shares_owned.LEMEDIATV_SHARES_SCIC
                              } parts sociales pour devenir Socio.`,
                      },
                      max: {
                        value:
                          1000 - account.shares_owned.LEMEDIATV_SHARES_SCIC,
                        message:
                          "Vous ne pouvez pas posséder plus de 1000 parts sociales.",
                      },
                    }}
                    error={errors.shares !== undefined}
                    helperText={errors.shares && errors.shares.message}
                  />
                </Shares>
                {/* {formValues.shares > 0 && (
                  <p
                    style={{
                      fontSize: "1.7rem",
                      marginTop: "28px",
                      textAlign: "center",
                    }}
                  >
                    Pour {formValues.shares}{" "}
                    {formValues.shares === "1"
                      ? "part sociale"
                      : "parts sociales"}
                    , je bénéficie d'une défiscalisation jusqu'à{" "}
                    <strong>
                      {numeral(formValues.shares / 2).format("0.00")}€
                    </strong>
                    .
                  </p>
                )} */}
              </section>
            )}
            <section>
              <h3>
                Quel <strong>moyen de paiement</strong> ?
              </h3>
              <RadioField>
                <input
                  type="radio"
                  name="payment_method"
                  value="SEPA"
                  id="payment_method_SEPA"
                  ref={register({ required: true })}
                />
                <label htmlFor="payment_method_SEPA">
                  {capitalizeFirstLetter(paymentMethods["SEPA"])}
                </label>
                <input
                  type="radio"
                  name="payment_method"
                  value="CREDIT_CARD"
                  id="payment_method_CREDIT_CARD"
                  ref={register({ required: true })}
                />
                <label htmlFor="payment_method_CREDIT_CARD">
                  {capitalizeFirstLetter(paymentMethods["CREDIT_CARD"])}
                </label>
                <input
                  type="radio"
                  name="payment_method"
                  value="CHECK"
                  id="payment_method_CHECK"
                  ref={register({ required: true })}
                  disabled={
                    !account.active_subscription &&
                    formValues.interval === "MONTH"
                  }
                />
                <label
                  htmlFor="payment_method_CHECK"
                  title={
                    formValues.interval === "MONTH"
                      ? "Seulement disponible pour les abonnements annuels."
                      : ""
                  }
                >
                  {capitalizeFirstLetter(paymentMethods["CHECK"])}
                </label>
              </RadioField>
              <Payment>
                {formValues.payment_method === "SEPA" && <SEPAPayment />}
                {formValues.payment_method === "CREDIT_CARD" && <CardPayment />}
                {formValues.payment_method === "CHECK" && (
                  <CheckPayment
                    amount={
                      parseInt(formValues.price || 0, 10) +
                      parseInt(formValues.shares || 0, 10)
                    }
                    email={account.email}
                  />
                )}
              </Payment>
            </section>
            <Validation>
              <h3>
                Je valide <strong>ma contribution</strong>
              </h3>
              <ul>
                {!account.active_subscription && (
                  <li>
                    <i className="far fa-bullhorn fa-fw" />
                    Je m'abonne au Média pour{" "}
                    <strong>
                      {numeral(parseInt(formValues.price, 10)).format("0.00")}€
                    </strong>
                    / {formValues.interval === "MONTH" ? "mois" : "an"}.
                  </li>
                )}
                {formValues.shares > 0 && (
                  <>
                    <li>
                      <i className="far fa-ticket-alt fa-fw" />
                      J'achète {formValues.shares}{" "}
                      {formValues.shares === "1"
                        ? "part sociale"
                        : "parts sociales"}{" "}
                      pour un total de <strong>{formValues.shares}€</strong>.
                    </li>
                    <li>
                      <i className="far fa-percent fa-fw" />
                      Mes parts sociales m'ouvrent le droit à une
                      défiscalisation jusqu'à{" "}
                      {numeral(formValues.shares / 2).format("0.00")}€.
                    </li>
                  </>
                )}
                {isSocioPath && formValues.interval === "MONTH" && (
                  <li>
                    <i
                      className="far fa-arrow-alt-right"
                      style={{ color: colors.purple }}
                    />{" "}
                    Je paie donc{" "}
                    <strong>
                      {numeral(
                        parseInt(formValues.price, 10) +
                          parseInt(formValues.shares, 10)
                      ).format("0.00")}
                      €
                    </strong>{" "}
                    ce mois-ci et{" "}
                    {numeral(parseInt(formValues.price, 10)).format("0.00")}€
                    les prochains mois.
                  </li>
                )}
                {isSocioPath && formValues.interval === "YEAR" && (
                  <li>
                    <i
                      className="far fa-arrow-alt-right"
                      style={{ color: colors.purple }}
                    />{" "}
                    Je paie donc{" "}
                    <strong>
                      {numeral(
                        parseInt(formValues.price, 10) +
                          parseInt(formValues.shares, 10)
                      ).format("0.00")}
                      €
                    </strong>{" "}
                    cette année.
                  </li>
                )}
              </ul>
              <fieldset>
                <Controller
                  as={Checkbox}
                  label={
                    <>
                      J'ai lu et j'approuve les{" "}
                      <Link to="/annexes/cgu">
                        conditions générales d'utilisation
                      </Link>{" "}
                      du Média.
                    </>
                  }
                  name="cgu"
                  control={control}
                  rules={{ validate: (val) => val === true }}
                  error={errors.cgu !== undefined}
                  helperText={
                    errors.cgu &&
                    "Vous devez approuver les conditions générales d'utilisation."
                  }
                />
              </fieldset>
              {(isSocioPath || isSharesPath) && (
                <fieldset>
                  <Controller
                    as={Checkbox}
                    label={
                      <>
                        J’ai lu et j'approuve les{" "}
                        <Link to="/annexes/statuts">statuts</Link> et le{" "}
                        <Link to="/annexes/reglement-interieur">
                          règlement intérieur
                        </Link>{" "}
                        du Média.
                      </>
                    }
                    name="rules"
                    control={control}
                    rules={{ validate: (val) => val === true }}
                    error={errors.rules !== undefined}
                    helperText={
                      errors.rules &&
                      "Vous devez approuver les statuts et le règlement intérieur."
                    }
                  />
                </fieldset>
              )}
            </Validation>
            {order.error && (
              <p className="error" style={{ marginTop: "30px" }}>
                {order.error}
              </p>
            )}
            <Button type="submit">
              {isSubscriptionPath && "Je valide mon abonnement"}
              {isSocioPath &&
                (account.shares_owned.LEMEDIATV_SHARES_SCIC < 5
                  ? "Je deviens Socio"
                  : "Je redeviens Socio")}
              {isSharesPath && "J'achète des parts sociales"}
            </Button>

            {formValues.payment_method === "SEPA" && <SEPAPaymentMandate />}
          </form>
        </SupportMain>
        <SupportAside>
          {isSubscriptionPath && <SubscriptionOffer />}
          {isSocioPath && (
            <SocioOffer
              isResubscribing={account.shares_owned.LEMEDIATV_SHARES_SCIC >= 5}
            />
          )}
          {isSharesPath && <SharesOffer />}
        </SupportAside>
      </SupportContent>
      <GridWrapper style={{ textAlign: "left" }}>
        <FAQ selected={selectedFAQ} />
      </GridWrapper>
    </Container>
  );
}

SupportPayment.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      path: PropTypes.oneOf(["abonnement", "socio", "parts-sociales"])
        .isRequired,
    }).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  subscriptionPlans: PropTypes.shape({
    list: PropTypes.array,
  }).isRequired,
  order: PropTypes.shape({
    isFetching: PropTypes.bool.isRequired,
    error: PropTypes.string,
  }).isRequired,
  account: PropTypes.shape({
    firstname: PropTypes.string.isRequired,
    lastname: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    shares_owned: PropTypes.shape({
      LEMEDIATV_SHARES_SCIC: PropTypes.number.isRequired,
    }).isRequired,
    active_subscription: PropTypes.object,
  }).isRequired,
};

export default SupportPayment;
